import React, { useState, useCallback } from 'react';
import { BannerProps } from '@/types/banner';
import BannerCloseButton from './BannerCloseButton';

const Banner: React.FC<BannerProps> = ({
  data,
  position,
  fullScreen,
  onClose,
  onClick,
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // デバイス判定
  const isMobile = () => {
    if (typeof navigator !== 'undefined' && navigator.userAgent.match(/iPhone|Android.+Mobile/)) {
      return true;
    }
    return false;
  };

  // 画像URL選択
  const getImageUrl = useCallback(() => {
    if (isMobile() && data.imageUrlMobile) {
      return data.imageUrlMobile;
    }
    return data.imageUrl;
  }, [data.imageUrl, data.imageUrlMobile]);

  // バナーサイズ計算
  const getBannerSize = () => {
    const size = data.size || 'small'; // デフォルトはsmall
    
    // サイズ別の定義
    const sizeMap = {
      small: {
        mini: { width: 80, height: 80 },
        full: { width: 140, height: 140 },
        mobileFull: { width: 120, height: 120 },
      },
      medium: {
        mini: { width: 120, height: 120 },
        full: { width: 200, height: 200 },
        mobileFull: { width: 160, height: 160 },
      },
      large: {
        mini: { width: 140, height: 140 },
        full: { width: 240, height: 240 },
        mobileFull: { width: 200, height: 200 },
      },
    };
    
    const sizeConfig = sizeMap[size];
    
    if (fullScreen) {
      // モバイルフルサイズ時は画面幅に合わせて大きめに表示
      if (isMobile()) {
        return sizeConfig.mobileFull;
      }
      return sizeConfig.full;
    }
    return sizeConfig.mini;
  };

  // 位置スタイル計算
  const getPositionStyle = (): React.CSSProperties => {
    // フルスクリーン時のポジションマップ
    const FULLSCREEN_POSITION_MAP = {
      1: { top: '7%', left: '4%' },      // 左上
      2: { top: '7%', left: '50%', transform: 'translateX(-50%)' },  // 中央上
      3: { top: '7%', right: '4%' },     // 右上
      4: { top: '33%', left: '4%', transform: 'translateY(-50%)' },  // 左中央
      5: { top: '33%', left: '50%', transform: 'translate(-50%, -50%)' },  // 中央
      6: { top: '33%', right: '4%', transform: 'translateY(-50%)' },     // 右中央
    };

    // ミニプレイヤー時のポジションマップ
    const MINI_POSITION_MAP = {
      1: { top: '10%', left: '4%' },      // 左上
      2: { top: '10%', left: '50%', transform: 'translateX(-50%)' },  // 中央上
      3: { top: '10%', right: '4%' },     // 右上
      4: { top: '40%', left: '4%', transform: 'translateY(-50%)' },  // 左中央
      5: { top: '40%', left: '50%', transform: 'translate(-50%, -50%)' },  // 中央
      6: { top: '40%', right: '4%', transform: 'translateY(-50%)' },     // 右中央
    };

    const POSITION_MAP = fullScreen ? FULLSCREEN_POSITION_MAP : MINI_POSITION_MAP;
    const positionKey = position as keyof typeof POSITION_MAP;
    const baseStyle = POSITION_MAP[positionKey] || POSITION_MAP[5];

    return {
      position: 'absolute' as const,
      ...baseStyle,
      marginTop: data.margin?.top || 0,
      marginLeft: data.margin?.left || 0,
      marginRight: data.margin?.right || 0,
      marginBottom: data.margin?.bottom || 0,
    };
  };

  // バナークリック処理
  const handleBannerClick = useCallback(() => {
    onClick(data.id, data.type, data.imageLink, data.clipText);
  }, [data.id, data.type, data.imageLink, data.clipText, onClick]);

  // バナー閉じる処理
  const handleClose = useCallback(() => {
    onClose(data.id);
  }, [data.id, onClose]);

  // 画像エラー処理
  const handleImageError = useCallback(() => {
    setImageError(true);
    console.warn(`Failed to load banner image: ${getImageUrl()}`);
  }, [getImageUrl]);

  // 画像読み込み完了処理
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const bannerSize = getBannerSize();
  const positionStyle = getPositionStyle();

  if (imageError) {
    return (
      <div
        data-banner-id={data.id}
        style={{
          ...positionStyle,
          width: bannerSize.width,
          height: bannerSize.height,
          pointerEvents: 'auto',
          zIndex: 201,
        }}
        className="tw-cursor-pointer tw-select-none tw-bg-gray-300 tw-rounded-lg tw-flex tw-items-center tw-justify-center"
        onClick={handleBannerClick}
      >
        <div className="tw-text-center tw-text-xs tw-text-gray-600">
          <div>画像読み込み</div>
          <div>エラー</div>
        </div>
        <BannerCloseButton onClose={handleClose} />
      </div>
    );
  }

  return (
    <div
      data-banner-id={data.id}
      style={{
        ...positionStyle,
        width: bannerSize.width,
        height: bannerSize.height,
        // バナー自体のみクリック可能
        pointerEvents: 'auto',
        zIndex: 201,
      }}
      className="tw-cursor-pointer tw-select-none tw-transition-all tw-duration-300 tw-hover:scale-105"
      onClick={handleBannerClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleBannerClick();
        }
      }}
      aria-label={`バナー: ${data.type === 'link' ? 'リンクを開く' : 'クリップする'}`}
    >
      {/* 画像 */}
      <img
        src={getImageUrl()}
        alt="バナー"
        className="tw-w-full tw-h-full tw-object-cover tw-rounded-lg tw-shadow-lg"
        onError={handleImageError}
        onLoad={handleImageLoad}
        style={{
          opacity: imageLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
      />

      {/* 画像エラー表示 */}
      {imageError && (
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-gray-300 tw-rounded-lg">
          <div className="tw-text-center tw-text-xs tw-text-gray-600">
            <div>画像読み込み</div>
            <div>エラー</div>
          </div>
        </div>
      )}

      {/* 閉じるボタン */}
      <BannerCloseButton 
        onClose={handleClose} 
        position={position}
        fullScreen={fullScreen}
      />
    </div>
  );
};

export default Banner; 